import React, { useMemo, useState } from 'react';
import { Bot as BotIcon } from 'lucide-react';
import { parseTranscript } from 'utils/helpers';
import TranscriptMessage from './TranscriptMessage';
import { TranscriptMessage as TranscriptMessageType } from 'types';

// A "landing" assistant turn is one whose FRT belongs to the real reply: not an
// error, not a placeholder, not a pre-ack filler (filler FRT is logged separately
// per the spec), and audio reached the caller (mid-audio counts — partial is heard).
const isLandingAssistant = (m: TranscriptMessageType): boolean =>
  m.role === 'assistant' &&
  !m.error &&
  !m.cancelled_placeholder &&
  !m.is_filler &&
  m.interruption_type !== 'before_audio';

const formatMs = (ms: number): string => (ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${Math.round(ms)}ms`);

type FrtField =
  | 'frt_ms'
  | 'stt_ms'
  | 'autocorrect_ms'
  | 'debounce_ms'
  | 'hallucination_ms'
  | 'llm_ms'
  | 'synth_ms'
  | 'silent_ms';

const FRT_LABELS: Array<[FrtField, string]> = [
  ['frt_ms', 'FRT'],
  ['stt_ms', 'STT'],
  ['autocorrect_ms', 'AC'],
  ['debounce_ms', 'Deb'],
  ['hallucination_ms', 'Hal'],
  ['llm_ms', 'LLM'],
  ['synth_ms', 'Synth'],
  ['silent_ms', 'Sil'],
];

interface TranscriptViewerProps {
  transcriptJson: string | null | undefined;
  title?: string;
  containerClassName?: string;
  /**
   * The call's audio, rendered WITH the conversation rather than in a block of
   * its own somewhere else on the page.
   *
   * Listening and reading are one task — you play a turn to hear how something
   * was said — and the player used to sit in a separate "Recording" section
   * above a wall of metadata, so on a long modal the two were never on screen
   * together. A slot here keeps them adjacent wherever the viewer is used.
   */
  player?: React.ReactNode;
}

const TranscriptViewer: React.FC<TranscriptViewerProps> = ({
  transcriptJson,
  title = 'AI Conversation Transcript',
  containerClassName = 'bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg space-y-3',
  player,
}) => {
  const [hideUnheard, setHideUnheard] = useState(false);
  const [hideSystem, setHideSystem] = useState(false);
  const [showRawStt, setShowRawStt] = useState(false);

  const messages = useMemo(() => parseTranscript(transcriptJson), [transcriptJson]);

  // Per-message context: for each landing assistant turn, count user turns
  // since the previous landing reply (chain depth).
  const contexts = useMemo(() => {
    if (!messages) return [] as Array<{ chainLength?: number }>;
    const out: Array<{ chainLength?: number }> = [];
    let userTurnsSinceLastLanding = 0;
    for (const m of messages) {
      if (m.role === 'user') {
        userTurnsSinceLastLanding += 1;
        out.push({});
      } else if (isLandingAssistant(m)) {
        out.push({ chainLength: userTurnsSinceLastLanding });
        userTurnsSinceLastLanding = 0;
      } else {
        out.push({});
      }
    }
    return out;
  }, [messages]);

  // Per-call latency averages over landing assistant turns with frt_ms.
  const averages = useMemo(() => {
    if (!messages) return null;
    const landing = messages.filter((m) => isLandingAssistant(m) && m.frt_ms != null);
    if (landing.length === 0) return null;
    const sums: Record<FrtField, number> = {
      frt_ms: 0, stt_ms: 0, autocorrect_ms: 0, debounce_ms: 0,
      hallucination_ms: 0, llm_ms: 0, synth_ms: 0, silent_ms: 0,
    };
    const counts: Record<FrtField, number> = {
      frt_ms: 0, stt_ms: 0, autocorrect_ms: 0, debounce_ms: 0,
      hallucination_ms: 0, llm_ms: 0, synth_ms: 0, silent_ms: 0,
    };
    for (const m of landing) {
      FRT_LABELS.forEach(([field]) => {
        const v = m[field];
        if (typeof v === 'number') {
          sums[field] += v;
          counts[field] += 1;
        }
      });
    }
    const avg: Partial<Record<FrtField, number>> = {};
    FRT_LABELS.forEach(([field]) => {
      if (counts[field] > 0) avg[field] = sums[field] / counts[field];
    });
    return { avg, n: landing.length };
  }, [messages]);

  if (!messages || messages.length === 0) return null;

  const visible = messages
    .map((msg, originalIndex) => ({ msg, originalIndex }))
    .filter(({ msg }) => {
      if (hideUnheard && msg.interruption_type === 'before_audio') return false;
      if (hideUnheard && msg.cancelled_placeholder) return false;
      if (hideSystem && msg.role === 'system') return false;
      return true;
    });

  return (
    <div>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <h4 className="font-semibold flex items-center gap-2">
          <BotIcon className="w-4 h-4 text-purple-600" />
          {title}
          <span className="text-[10px] text-gray-500 font-normal">
            ({visible.length} of {messages.length})
          </span>
        </h4>
        <div className="flex items-center gap-3 text-[10px] text-gray-600">
          <label className="flex items-center gap-1 cursor-pointer">
            <input
              type="checkbox"
              checked={hideUnheard}
              onChange={(e) => setHideUnheard(e.target.checked)}
              className="w-3 h-3"
            />
            Hide unheard
          </label>
          <label className="flex items-center gap-1 cursor-pointer">
            <input
              type="checkbox"
              checked={hideSystem}
              onChange={(e) => setHideSystem(e.target.checked)}
              className="w-3 h-3"
            />
            Hide system
          </label>
          <label className="flex items-center gap-1 cursor-pointer">
            <input
              type="checkbox"
              checked={showRawStt}
              onChange={(e) => setShowRawStt(e.target.checked)}
              className="w-3 h-3"
            />
            Show raw STT
          </label>
        </div>
      </div>
      {averages && (
        <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 rounded border border-gray-200 bg-white text-[11px] text-gray-700">
          <span className="font-semibold text-gray-800">Avg latency</span>
          <span className="text-gray-500">over {averages.n} landing {averages.n === 1 ? 'reply' : 'replies'}</span>
          <span className="h-3 w-px bg-gray-300" />
          {FRT_LABELS.map(([field, label]) => {
            const v = averages.avg[field];
            if (v == null) return null;
            return (
              <span key={field} className={field === 'frt_ms' ? 'font-semibold text-gray-900' : ''}>
                {label} <span className="font-mono">{formatMs(v)}</span>
              </span>
            );
          })}
        </div>
      )}
      <div className={containerClassName}>
        {/* At the TOP of the conversation, not the foot of the modal. */}
        {player && <div className="mb-3">{player}</div>}
        {visible.map(({ msg, originalIndex }) => {
          const ctx = contexts[originalIndex] || {};
          return (
            <TranscriptMessage
              key={originalIndex}
              message={msg}
              showRawStt={showRawStt}
              chainLength={ctx.chainLength}
            />
          );
        })}
      </div>
    </div>
  );
};

export default TranscriptViewer;
